import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { EvalService } from "@/gen/omnios/v1/eval_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Lock, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/settings/eval-datasets")({
	component: EvalDatasetsSettings,
});

const evalClient = createClient(EvalService, transport);

function EvalDatasetsSettings() {
	const { t } = useTranslation("eval");
	const qc = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["eval", "datasets"],
		queryFn: () => evalClient.listEvalDatasets({ pageSize: 100 }),
	});

	const createMut = useMutation({
		mutationFn: () =>
			evalClient.createEvalDataset({ name, description, kind: "golden_match" }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["eval", "datasets"] });
			setCreateOpen(false);
			setName("");
			setDescription("");
			toast.success(t("dataset_created"));
		},
	});

	return (
		<div className="space-y-6">
			<div className="flex justify-end">
				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="mr-1 h-4 w-4" />
							{t("dataset_create")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("dataset_create_title")}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label>{t("dataset_name")}</Label>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder={t("dataset_name_placeholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("dataset_description")}</Label>
								<Input
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder={t("dataset_description_placeholder")}
								/>
							</div>
							<Button
								className="w-full"
								onClick={() => createMut.mutate()}
								disabled={!name || createMut.isPending}
							>
								{t("dataset_create")}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</div>

			{isLoading ? (
				<Skeleton className="h-48" />
			) : !data?.datasets?.length ? (
				<Card>
					<CardContent className="py-8 text-center text-muted-foreground">
						{t("dataset_empty")}
					</CardContent>
				</Card>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("dataset_name")}</TableHead>
								<TableHead>{t("col_version")}</TableHead>
								<TableHead>{t("dataset_rows")}</TableHead>
								<TableHead>{t("col_status")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.datasets.map((ds) => (
								<TableRow key={ds.id}>
									<TableCell>
										<Link
											to="/settings/eval-datasets/$id"
											params={{ id: ds.id }}
											className="font-medium text-primary hover:underline"
										>
											{ds.name}
										</Link>
									</TableCell>
									<TableCell className="font-mono text-xs">
										v{ds.version}
									</TableCell>
									<TableCell>{ds.rowCount}</TableCell>
									<TableCell>
										{ds.frozenAt ? (
											<Badge variant="secondary" className="text-xs">
												<Lock className="mr-1 h-3 w-3" />
												{t("dataset_frozen_badge")}
											</Badge>
										) : (
											<Badge variant="outline" className="text-xs">
												{t("dataset_active_badge")}
											</Badge>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
